import { axios, utils } from 'ynf-core-middleware';
import { isType } from '../type';
import domainKeyMscMap from './domainKey-msc.json'; // domainkey 映射
import { omitBy } from 'lodash'
import { getYMSHost } from '@utils';

declare module 'ynf-core-middleware/*';
declare interface axiosOptions {
  url: string;
  method: string;
  data: Object | Array<any> | string | null;
  params: Object;
}
declare interface axiosResult {
  status: number;
  data: Object | Array<any>;
  headers: Object
}
/**
 * 添加通用Headers
 */
const commonHeaders : any  = {
  // 'x-network-type': 'public', // 服务去 node后确实该参数，该参数之前默认有mdf-node统一添加
}

const axiosInstance = axios.create({
  // @ts-ignore
  baseURL: getYMSHost(),
  timeout: 20000
});

// 添加请求拦截器
axiosInstance.interceptors.request.use(function (request: any) {
  /**
   * 发请求之前的拦截器
   *
   * 添加默认的 header 参数
   */
  Object.keys(commonHeaders).map( (key: string) => request.headers[key] = commonHeaders[key])
  /**
   * 发请求之前的拦截器
   *
   * 判断 cookie中的 authKey 是否存在，
   * 1、如果存在，则添加自定义的 Header
   * 请求头：'dynamic-auth-token'：$authKey
   */
   const urlParams = request.params || {}
  // 打开待办才需要传动态token
  if (urlParams.from_mc_workflow && urlParams.billId) {
    const keys = [`dynamic_auth_token_${urlParams.billId}`, 'x_change_info', 'x_a01']
    const headersKey = ['dynamic-auth-token', 'x-change-info', 'x-a01']
    keys.forEach((key, index) => {
      if (window.sessionStorage.getItem(key)) {
        request.headers[headersKey[index]] = window.sessionStorage.getItem(key)
      }
    })
  }
  // 在发送请求之前做些什么
  return request;
}, function (error: Error) {
  // 对请求错误做些什么
  return Promise.reject(error);
});

// 添加响应拦截器
axiosInstance.interceptors.response.use(function (response: any) {
  // 2xx 范围内的状态码都会触发该函数。
  // 对响应数据做点什么
  if (response.headers['x-a01']) {
    window.sessionStorage.setItem('x_a01', response.headers['x-a01']);
    if (response.data) response.data['x-a01'] = response.headers['x-a01']
  }
  if (isType(response.data, 'Object')) {
    if (response.data.msg &&
      !response.data.message) {
      response.data.message = response.data.msg;
    }
    if (!response.data.message) {
      response.data.message = `URL: "${ response.config.url }" Code: "${ response.data.code ?? response.status }"`
    }
  }
  return response;
}, function (error: Error) {
  // 超出 2xx 范围的状态码都会触发该函数。
  // 对响应错误做点什么
  return Promise.reject(error);
});

/**
 * 获取微服务编码
 * @param domainKey 
 */
export function getMscByDomainKey(domainKey = '') {
  if (typeof domainKey !== 'string') return domainKey;
  // domainKey 映射
  if (Reflect.has(domainKeyMscMap, domainKey)) {
    domainKey = Reflect.get(domainKeyMscMap, domainKey);
  }
  // mdd|ypd 虚拟 domainKey
  return domainKey.replace(/-(mdd|ypd)/g, '');
}

/**
 * request
 * @param options 
 * @returns 
 */
async function request (options: axiosOptions) {
  try {
    let { url, method = 'post', params = {}, data = {}, ...rest } = options;

    // parse domainkey
    const domainKey = Reflect.get(params, 'domainKey') || utils.getQueryParam(url, 'domainKey');
    if (domainKey) {
      const msc = getMscByDomainKey(domainKey);
      url = url.replace(/^\/mdf-node\/qtmProxy/, `/${msc}`)
    }

    if (data?.constructor === Object) data = omitBy(data, (value: any) => value?.storeType)
    if (params?.constructor === Object) params = omitBy(params, (value: any) => typeof value === 'function' || value instanceof Object || Array.isArray(value))

    // 小程序里走mtl原生发送请求
    if (window.location.href.startsWith('file://')) {
      return mtlRequest(url, method, params, data)
    }
    
    const res: axiosResult = await axiosInstance({
      url,
      method,
      data,
      params,
      ...rest
    })
    if ([200, 304].includes(res.status)) {
      if (isType(res.data, 'Object')) {
        // @ts-ignore
        // 打印的接口res.data.status返回的是1
        if ([200, 1, 0].includes(Number(res.data.code ?? res.data.status ?? 200))) {
          console.groupCollapsed(`【LOG-Request】Axios Success(${ url })`);
          console.log(`url: `, url);
          console.log(`response.status: `, res.status);
          // @ts-ignore
          console.log(`response.data.code: `, res.data.code);
          console.log(`response.data: `, res.data);
          console.log(`response.headers: `, res.headers);
          console.log(`response: `, res);
          console.trace();
          console.groupEnd();
          return Promise.resolve({ ...res.data, headers: res.headers });
        } else {
          console.error(`Axios Error. `, res.data, res);
          return Promise.reject(res.data);
        }
      } else {
        console.groupCollapsed(`【LOG-Request】Axios Success(${ url })`);
        console.log(`url: `, url);
        console.log(`response.status: `, res.status);
        console.log(`response.headers: `, res.headers);
        console.log(`response: `, res);
        console.trace();
        console.groupEnd();
        return Promise.resolve(res);
      }
    } else {
      console.error(`Axios Error. `, res);
      return Promise.reject(res.data);
    }
  } catch (err) {
    console.error(`Axios Error. `, err);
    return Promise.reject(err);
  }
}

function mtlRequest(url: string, method: string, params: any, data: any) {
  // 处理url
  const search = Object.keys(params).map(key => `${key}=${params[key] || ''}`).join('&')
  if (url.includes('?')) {
    url = getYMSHost() + url + '&' + search
  } else {
    url = getYMSHost() + url + '?' + search
  }
  // 处理header
  const headers = {}
  if (window.sessionStorage.getItem('yhtToken')) {
    headers.yht_access_token = window.sessionStorage.getItem('yhtToken')
  }
  return new Promise((resolve, reject) => {
    window.mtl.request({
      url,
      method,
      headers,
      params: data,
      paramsType: method.toLocaleUpperCase() === 'POST' ? 1 : 0,
      success: function(res: any) {
        // 原生res.data是json串，做转义处理
        if (!isType(res.data, 'Object')) {
          res.data = JSON.parse(res.data)
        }
        if ([200, 304].includes(res.status)) {
          if (isType(res.data, 'Object')) {
            if ([200, 0].includes(Number(res.data.code ?? res.data.status ?? 200))) {
              console.groupCollapsed(`【LOG-Request】mtlRequest Success(${ url })`);
              console.log(`url: `, url);
              console.log(`response.status: `, res.status);
              console.log(`response.data.code: `, res.data.code);
              console.log(`response.data: `, res.data);
              console.log(`response.headers: `, res.headers);
              console.log(`response: `, res);
              console.trace();
              console.groupEnd();
              resolve({ ...res.data, headers: res.headers });
            } else {
              console.error(`mtlRequest Error. `, res.data, res);
              reject(res.data);
            }
          } else {
            console.groupCollapsed(`【LOG-Request】mtlRequest Success(${ url })`);
            console.log(`url: `, url);
            console.log(`response.status: `, res.status);
            console.log(`response.headers: `, res.headers);
            console.log(`response: `, res);
            console.trace();
            console.groupEnd();
            resolve(res);
          }
        } else {
          console.error(`mtlRequest Error. `, res);
          reject(res.data);
        }
      },
      fail: function(err: any) {
        console.error(`mtlRequest Error. `, err);
        reject(err)
      }
    });
  })
}
export default request;
